import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { AppError, ToastOptions, ErrorSeverity } from '@/types/errors';
import { ErrorContext } from './useErrorContext';

/** Default auto-dismiss timeout in milliseconds */
const DEFAULT_AUTO_DISMISS_MS = 5000;

/** Maximum number of toasts to display at once */
const MAX_TOASTS = 5;

/**
 * Generate a unique ID for an error.
 */
function generateErrorId(): string {
  return `error-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Determine if an error should auto-dismiss based on severity.
 */
function shouldAutoDismiss(severity: ErrorSeverity, explicit?: boolean): boolean {
  if (explicit !== undefined) return explicit;
  // Info and warning auto-dismiss by default, errors persist
  return severity !== 'error';
}

interface ErrorProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the global error reporting system.
 * Manages error state and provides functions to show/dismiss errors.
 */
export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errors, setErrors] = useState<AppError[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
    };
  }, []);

  const dismissError = useCallback((id: string) => {
    // Clear any pending timer
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const addError = useCallback(
    (message: string, severity: ErrorSeverity, options?: ToastOptions): string => {
      const id = generateErrorId();
      const autoDismiss = shouldAutoDismiss(severity, options?.autoDismiss);
      const autoDismissMs = options?.autoDismissMs ?? DEFAULT_AUTO_DISMISS_MS;

      const newError: AppError = {
        id,
        message,
        severity,
        code: options?.code,
        traceId: options?.traceId,
        details: options?.details,
        context: options?.context,
        timestamp: Date.now(),
        autoDismiss,
        autoDismissMs,
      };

      setErrors((prev) => {
        // Deduplicate: if same message and context exists, don't add
        const isDuplicate = prev.some((e) => e.message === message && e.context === options?.context);
        if (isDuplicate) {
          return prev;
        }

        // Limit max toasts, remove oldest first
        const updated = [...prev, newError];
        if (updated.length > MAX_TOASTS) {
          const removed = updated.shift();
          if (removed) {
            const timer = timersRef.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(removed.id);
            }
          }
        }
        return updated;
      });

      // Set auto-dismiss timer if enabled
      if (autoDismiss) {
        const timer = setTimeout(() => {
          dismissError(id);
        }, autoDismissMs);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismissError],
  );

  const showError = useCallback(
    (message: string, options?: ToastOptions): string => {
      return addError(message, options?.severity ?? 'error', options);
    },
    [addError],
  );

  const showToast = useCallback(
    (message: string, options?: ToastOptions): string => {
      return addError(message, options?.severity ?? 'info', options);
    },
    [addError],
  );

  const clearAllErrors = useCallback(() => {
    // Clear all timers
    for (const timer of timersRef.current.values()) {
      clearTimeout(timer);
    }
    timersRef.current.clear();

    setErrors([]);
  }, []);

  const value = {
    errors,
    showError,
    showToast,
    dismissError,
    clearAllErrors,
  };

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
}
